import type { UnmappedItem } from '../services';
import { FILTER_CONFIGS } from './constants';
import type { FilterConfig } from './types';

// Every spelling of a dimension that can arrive in `unmapped[].filter`. The backend
// normalises to the camelCase dimension key (NPJB-398), but apiType is accepted too so
// a regression there degrades into "warn about it" rather than a silent misroute.
// jobTitle's apiType is empty on purpose (free-text field, no lookup) — skip it, or
// every unclassifiable value would resolve to Job Title.
const CONFIG_BY_TOKEN = new Map<string, FilterConfig>();
for (const config of FILTER_CONFIGS) {
  CONFIG_BY_TOKEN.set(config.key.toLowerCase(), config);
  if (config.apiType) CONFIG_BY_TOKEN.set(config.apiType.toLowerCase(), config);
}

export interface UnmappedPartition {
  /**
   * Filter key → off-taxonomy labels to auto-create as custom values on that filter.
   *
   * Iteration order is FILTER_CONFIGS order, NOT the order the backend listed the values
   * (NPJB-608). Callers apply these by iterating the Map, and a dependent dimension has to
   * be written after its parent: `PARENT_REQUIRED_KEYS` in audience-store refuses a custom
   * sub-industry while `industry` is still empty, so a brief returning a custom value for
   * both levels would silently lose the sub if the backend happened to list it first.
   */
  autoCreate: Map<string, string[]>;
  /**
   * Still shown to the user as warnings: values whose dimension is known but cannot
   * hold custom entries, plus values with no determinable dimension. Kept as one list
   * because it is exactly what the UI store carries — the alert component splits it
   * again for rendering.
   */
  remaining: UnmappedItem[];
}

/**
 * Split a brief's unmapped values into "auto-create as a custom filter value" and
 * "tell the user about it" (NPJB-398).
 *
 * The dividing line is `allowCustom`, not merely whether the dimension is known.
 * Sub-Industry and State are closed taxonomies — a free-typed value there could only ever
 * be invalid data (see FILTER_CONFIGS), so they keep warning even though the backend told
 * us exactly which dimension the value belongs to. Industry was in that list until
 * NPJB-662 opened it to custom values; it now auto-creates like the rest.
 */
export function partitionUnmapped(items: UnmappedItem[]): UnmappedPartition {
  const byKey = new Map<string, string[]>();
  const remaining: UnmappedItem[] = [];

  for (const item of items) {
    const value = item.value.trim();
    if (value.length === 0) continue;

    const config =
      item.filter === null ? undefined : CONFIG_BY_TOKEN.get(item.filter.toLowerCase());
    // Unknown dimension OR a dimension that cannot hold custom entries — both warn.
    if (!config?.allowCustom) {
      remaining.push(item);
      continue;
    }

    const labels = byKey.get(config.key);
    if (labels === undefined) {
      byKey.set(config.key, [value]);
    } else {
      labels.push(value);
    }
  }

  // Re-key in FILTER_CONFIGS order so parents are applied before their dependents — see
  // the `autoCreate` doc above. Every key in `byKey` came from a FILTER_CONFIGS entry, so
  // this re-emits all of them and drops none.
  const autoCreate = new Map<string, string[]>();
  for (const config of FILTER_CONFIGS) {
    const labels = byKey.get(config.key);
    if (labels !== undefined) autoCreate.set(config.key, labels);
  }

  return { autoCreate, remaining };
}
